import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getClientDetails, getClientDocuments } from 'services/ClientService';

import { ApiStatus } from 'constants/enums';
import type { Client } from 'models/client.interface';
import type { Document } from 'models/document.interface';
import { handleApiErrors } from 'utils/errorUtils';

export interface ClientState {
  details: {
    data: Client | null;
    status: ApiStatus;
  },
  documents: {
    data: Document[] | null;
    status: ApiStatus;
  }
}

const initialState: ClientState = {
  details: {
    data: null,
    status: ApiStatus.idle,
  },
  documents: {
    data: null,
    status: ApiStatus.idle,
  }
};

export const fetchClientDetails = createAsyncThunk(
  'client/fetchClientDetails',
  async ({ clientId }: { clientId: string }, thunkAPI) => {
    try {
      return await getClientDetails(clientId);
    } catch (e) {
      handleApiErrors(e);
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const fetchClientDocuments = createAsyncThunk(
  'client/fetchClientDocuments',
  async ({ clientId }: { clientId: string }, thunkAPI) => {
    try {
      return await getClientDocuments(clientId);
    } catch (e) {
      handleApiErrors(e);
      return thunkAPI.rejectWithValue(e);
    }
  },
);

export const clientDetailsSlice = createSlice({
  name: 'clientDetails',
  initialState,
  reducers: {
    resetClientState: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchClientDetails.pending, (state) => {
        state.details = {
          ...state.details,
          status: ApiStatus.loading,
        };
      })
      .addCase(fetchClientDetails.fulfilled, (state, action) => {
        const client = action.payload;
        state.details = {
          data: client,
          status: ApiStatus.idle,
        };
      })
      .addCase(fetchClientDetails.rejected, (state) => {
        state.details = {
          ...state.details,
          status: ApiStatus.idle,
        };
      })
      .addCase(fetchClientDocuments.pending, (state) => {
        state.documents = {
          ...state.documents,
          status: ApiStatus.loading,
        };
      })
      .addCase(fetchClientDocuments.fulfilled, (state, action) => {
        const documents = action.payload;
        state.documents = {
          data: documents,
          status: ApiStatus.idle,
        };
      })
      .addCase(fetchClientDocuments.rejected, (state) => {
        state.documents = {
          ...state.documents,
          status: ApiStatus.idle,
        };
      });
  }
});

export const {
  resetClientState,
} = clientDetailsSlice.actions;

export default clientDetailsSlice.reducer;
